import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import Icon from '@atlaskit/icon';
import Tooltip from '@atlaskit/tooltip';
import deburr from 'lodash/deburr';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { unquote } from '../../../../utils/string';
import { checkboxGlyph, dateGlyph, dropdownGlyph, labelGlyph, numberGlyph, paragraphGlyph, peopleGlyph, shortTextGlyph, timeStampGlyph } from './glyphs';
import { FieldType, FieldTypeIcon, OptionHighlight, OptionListItem, OptionName, TooltipContent } from './styled';

var ResizedIcon = function ResizedIcon(_ref) {
  var glyph = _ref.glyph;
  return /*#__PURE__*/React.createElement(Icon, {
    glyph: glyph,
    size: "small",
    label: "",
    testId: "jql-editor-field-type-icon"
  });
};
/**
 * List of types with supported icons is derived from GIN
 * Custom SVGs are provisional while we work on making field type icons consistent across Atlassian
 */


var getFieldTypeIcon = function getFieldTypeIcon(type) {
  switch (type) {
    case 'Checkboxes':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: checkboxGlyph
      });

    case 'Date':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: dateGlyph
      });

    case 'Dropdown':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: dropdownGlyph
      });

    case 'Labels':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: labelGlyph
      });

    case 'Number':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: numberGlyph
      });

    case 'Paragraph':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: paragraphGlyph
      });

    case 'People':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: peopleGlyph
      });

    case 'Short text':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: shortTextGlyph
      });

    case 'Time stamp':
      return /*#__PURE__*/React.createElement(ResizedIcon, {
        glyph: timeStampGlyph
      });

    default:
      return null;
  }
};

var getHighlightPosition = function getHighlightPosition(name, matchedText) {
  var normalizedName = deburr(name).toLowerCase();
  var normalizedMatchedText = deburr(unquote(matchedText)).toLowerCase();
  var index = normalizedName.indexOf(normalizedMatchedText);
  if (index === -1) return null;
  return [index, index + normalizedMatchedText.length];
};

var AutocompleteOption = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$option = props.option,
      id = _props$option.id,
      name = _props$option.name,
      fieldType = _props$option.fieldType,
      matchedText = _props$option.matchedText,
      isSelected = props.isSelected,
      onClick = props.onClick,
      onMouseMove = props.onMouseMove;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      overflows = _useState2[0],
      setOverflows = _useState2[1];

  var onNameRef = useCallback(function (node) {
    if (node != null) {
      setOverflows(node.offsetWidth < node.scrollWidth);
    }
  }, []); // TODO: verify type is i18n'd after integrating with GraphQL API

  var fieldTypeIcon = fieldType ? getFieldTypeIcon(fieldType) : null;
  var optionName = useMemo(function () {
    var highlightPosition = getHighlightPosition(name, matchedText);
    return highlightPosition ? /*#__PURE__*/React.createElement(OptionName, {
      innerRef: onNameRef,
      "aria-label": name
    }, name.substring(0, highlightPosition[0]), /*#__PURE__*/React.createElement(OptionHighlight, null, name.substring.apply(name, _toConsumableArray(highlightPosition))), name.substring(highlightPosition[1])) : /*#__PURE__*/React.createElement(OptionName, {
      innerRef: onNameRef
    }, name);
  }, [onNameRef, name, matchedText]);
  var ListItem = /*#__PURE__*/React.createElement(OptionListItem, _extends({
    id: id,
    "data-testid": "jql-editor-autocomplete-option",
    role: "option",
    isSelected: isSelected,
    onClick: onClick,
    onMouseMove: onMouseMove
  }, ref && {
    innerRef: ref
  }, {
    "aria-selected": isSelected
  }), optionName, fieldType && /*#__PURE__*/React.createElement(FieldType, null, fieldTypeIcon && /*#__PURE__*/React.createElement(FieldTypeIcon, null, fieldTypeIcon), fieldType));
  var tooltipContent = /*#__PURE__*/React.createElement(TooltipContent, null, name);

  if (overflows) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      content: tooltipContent,
      position: 'right'
    }, ListItem);
  }

  return ListItem;
});
export default AutocompleteOption;